import axios from 'axios';

import { getConfig } from '../../../environmentConfig';
import { toBody } from '../utils';

const CLASS_ID_PARAM = 'classId';
const MAX_LOG_VALUE_LENGTH = 256;
const MAX_QUERY_PARAM_COUNT = 25;
const TRUNCATED_LOG_SUFFIX = '... [truncated]';
const NUMERIC_CLASS_ID = /^\d+$/;
const SENSITIVE_QUERY_PARAMS = new Set([
  'access_token',
  'classcode',
  'classname',
  'code',
  'displayname',
  'guid',
  'id_token',
  'name',
  'password',
  'pwd',
  'refresh_token',
  'ss',
  'teachername',
  'token',
  'user',
  'username',
]);

export const isNumericClassId = (classId?: string | null): boolean =>
  !!classId && NUMERIC_CLASS_ID.test(classId);

export const getSafeLogValue = (value: string): string => {
  return getTruncatedLogValue(JSON.stringify(value));
};

export const getTruncatedLogValue = (value: string): string =>
  value.length > MAX_LOG_VALUE_LENGTH
    ? `${value.slice(
        0,
        MAX_LOG_VALUE_LENGTH - TRUNCATED_LOG_SUFFIX.length
      )}${TRUNCATED_LOG_SUFFIX}`
    : value;

export const getSanitizedQueryParams = (
  search: string
): Record<string, string> => {
  const urlParams = new URLSearchParams(search);
  const params: Record<string, string> = {};
  let paramCount = 0;
  const queryParams = urlParams.entries();
  let queryParam = queryParams.next();

  while (!queryParam.done && paramCount < MAX_QUERY_PARAM_COUNT) {
    const [key, value] = queryParam.value;

    const sanitizedKey = getTruncatedLogValue(key);
    const sanitizedValue = SENSITIVE_QUERY_PARAMS.has(key.trim().toLowerCase())
      ? '[redacted]'
      : getTruncatedLogValue(value);

    Object.defineProperty(params, sanitizedKey, {
      configurable: true,
      enumerable: true,
      value: sanitizedValue,
      writable: true,
    });
    paramCount += 1;
    queryParam = queryParams.next();
  }

  return params;
};

export const getSanitizedReferrer = (referrer: string): string => {
  if (!referrer) return '[none]';

  try {
    const referrerUrl = new URL(referrer);

    referrerUrl.username = '';
    referrerUrl.password = '';
    referrerUrl.search = '';
    referrerUrl.hash = '';

    return referrerUrl.toString();
  } catch {
    return '[invalid referrer]';
  }
};

export const getInvalidClassIdDiagnosticMessage = (
  search: string,
  referrer: string
): string | undefined => {
  const urlParams = new URLSearchParams(search);
  const classId = urlParams.get(CLASS_ID_PARAM);

  if (classId === null || isNumericClassId(classId)) return undefined;

  return `Invalid login classId found in initial student URL
classId: ${getSafeLogValue(classId)}
sanitizedQueryParams: ${JSON.stringify(getSanitizedQueryParams(search))}
referrer: ${getSanitizedReferrer(referrer)}`;
};

export const reportInvalidClassIdFromInitialUrl = async (
  search?: string,
  referrer?: string
): Promise<void> => {
  const initialSearch =
    search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const initialReferrer =
    referrer ?? (typeof document !== 'undefined' ? document.referrer : '');
  const message = getInvalidClassIdDiagnosticMessage(
    initialSearch,
    initialReferrer
  );

  if (!message) return;

  try {
    await axios.post(
      `${getConfig().JAVA_URL}/ErrorToServer`,
      toBody({
        m: message,
        a: new Date().getTime(),
      })
    );
  } catch {
    // Best-effort diagnostic only.
  }
};
